import { MobXProviderContext } from 'mobx-react';
import { ContactModelBase } from 'Models/ContactModel';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { ParticipantModel } from 'Models/ParticipantModel';
import { useCallback, useContext, useEffect, useState } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { isNullOrUndefined } from 'util';

/**
 * Custom hook for loading and managing group member contacts
 *
 * This hook handles the logic of loading contacts for participants in a conversation.
 * It fetches participants by conversation ID, then loads the corresponding contact information
 * for each participant. Returns reactive contact data that updates automatically when
 * the underlying MobX stores change.
 *
 * @param conversationId - The ID of the conversation to load members for
 * @returns An object containing:
 *   - contacts: Array of loaded contact models (ContactModelV2 or ContactModelBase)
 *   - loadingContacts: Boolean indicating if contacts are currently being loaded
 *
 * @example
 * ```tsx
 * const { contacts, loadingContacts } = useGroupMemberContacts('conversation-123');
 *
 * if (loadingContacts) {
 *   return <LoadingSpinner />;
 * }
 *
 * return (
 *   <div>
 *     {contacts.map(contact => <ContactCard key={contact.id} contact={contact} />)}
 *   </div>
 * );
 * ```
 */
export const useGroupMemberContacts = (conversationId: string) => {
  const {
    participant: { selectParticipantsByConversationId },
    contact: contactStore,
    person: personStore,
  } = useContext<RootStoreProps>(MobXProviderContext);

  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);

  const participantsByConversationId =
    selectParticipantsByConversationId(conversationId);

  // Derive current participants from the store on every render so the list
  // updates when the store changes. (MobX bs)
  const currentParticipants: ParticipantModel[] =
    participantsByConversationId?.state === 'fulfilled'
      ? participantsByConversationId.value.data.results
      : [];

  /**
   * Loads contact information for a given participant
   * Triggers loading in the store but doesn't store the result
   *
   * @param participant - The participant model to load contact for
   */
  const loadContact = useCallback(
    async (participant: ParticipantModel) => {
      if (participant.personId) {
        await personStore.loadPersonByIdGetIfMissingGet(
          participant.personId.toString()
        );
      } else if (participant.phone) {
        await personStore.getAllContactsWithSameNum(participant.phone);
        await contactStore.loadContactByPhoneNumber(participant.phone);
      }
    },
    [contactStore, personStore]
  );

  // Trigger contact loading when conversation or participant list changes
  useEffect(() => {
    const loadContactsForParticipants = async (
      participants: ParticipantModel[]
    ) => {
      setLoadingContacts(true);
      await Promise.all(participants.map((p) => loadContact(p)));
      setLoadingContacts(false);
    };

    if (!isNullOrUndefined(participantsByConversationId)) {
      participantsByConversationId.case({
        fulfilled: (resp) => {
          void loadContactsForParticipants(resp.data.results);
        },
        pending: () => {
          setLoadingContacts(true);
        },
      });
    }
  }, [conversationId, loadContact, participantsByConversationId]);

  // Get contacts reactively from the store using current participants.
  // Reading currentParticipants during render keeps the list in sync when
  // the store updates (e.g. after ConversationLeave).
  const getContacts = (): (ContactModelV2 | ContactModelBase)[] => {
    const contactsArray: (ContactModelV2 | ContactModelBase)[] = [];

    currentParticipants.forEach(({ personId, phone }) => {
      if (personId) {
        // Get reactive contact from person store
        const contact = personStore.selectPersonValueById(personId);
        if (contact) {
          contactsArray.push(contact);
        }
      } else if (phone) {
        // Try to get saved contacts with same number
        const contactsPbo = personStore.getAllContactsWithSameNum(phone);
        const savedContacts =
          contactsPbo?.state === 'fulfilled' ? contactsPbo.value : null;

        if (savedContacts && savedContacts.length) {
          contactsArray.push(savedContacts[0]);
        } else {
          // Fall back to unsaved contact
          const contactPbo = contactStore.loadContactByPhoneNumber(phone);
          const unsavedContact =
            contactPbo?.state === 'fulfilled'
              ? (contactPbo.value as ContactModelBase)
              : null;

          if (unsavedContact) {
            contactsArray.push(unsavedContact);
          }
        }
      }
    });

    return contactsArray;
  };

  const contacts = getContacts();

  return {
    contacts,
    loadingContacts,
  };
};
